import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { VStack } from '@cbhq/cds-web/layout/VStack';
import { useToast } from '@cbhq/cds-web/overlays/useToast';
import { ActionType, ComponentType, logEvent } from '@cbhq/client-analytics';
import { useGetWorkspaceMembers } from '@cbhq/identity-api-hooks/account-management/fetch';
import type { CoinbaseEngUsersAccountManagementApiApiWorkspaceMember } from '@cbhq/identity-api-hooks/account-management/types';

import { InAppErrorMessageFallback } from '../../../components/ErrorBoundary/InAppErrorMessageFallback';
import { teamAccessMessages } from '../../../components/ErrorBoundary/messages';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useUserProfile } from '../../../contexts/UserProfileContext';
import { makeChipFilterChange } from '../../../utils/makeChipFilterChange';
import { getProductDisplayName } from '../components/InviteTray/productCatalog';
import type { StatusKey } from '../components/TeamFilters';
import { TeamFilters } from '../components/TeamFilters';
import { TeamHeader } from '../components/TeamHeader';
import { TeamTable } from '../components/TeamTable';
import type { AvatarColor, UserCellMember } from '../components/UserCell';
import type { Column } from '../types';
import { useTeamApi } from '../useTeamApi';
import { getCancelInviteErrorMessage } from '../utils/cancelInviteErrors';
import { canManageMembers, canRemoveMembers } from '../utils/canManageMembers';
import type { Member as TeamMember, MemberRole } from '../utils/constants';
import { getTeamMemberRoute, ROUTE_TEAM } from '../utils/constants';
import { getProductKey, useRoles } from '../utils/useRoles';

import { RemoveMember } from './RemoveMember';

const AVATAR_COLORS: AvatarColor[] = ['blue', 'teal', 'purple', 'pink', 'green', 'orange'];

const ALL_TAB_ID = 'All';
const ORG_COLUMNS: Column[] = ['name', 'product', 'entity', 'status'];
const ORG_LOGO_SRC = '';

const messages = defineMessages({
  noOrgSelectedTitle: {
    id: 'membersV2.noOrgSelectedTitle',
    defaultMessage: 'No organization selected',
    description: 'Title shown when no organization is selected on the Team page',
  },
  selectOrg: {
    id: 'membersV2.selectOrg',
    defaultMessage: 'Select an organization to view team members.',
    description: 'Empty state shown when no organization is selected on the Team page',
  },
  yourOrganization: {
    id: 'membersV2.yourOrganization',
    defaultMessage: 'Your Organization',
    description: 'Fallback org name displayed when the organization name is unavailable',
  },
});

const EMPTY_ARRAY: string[] = [];

function getAvatarColor(email: string): AvatarColor {
  let hash = 0;
  for (let index = 0; index < email.length; index += 1) {
    // eslint-disable-next-line no-bitwise
    hash = ((hash << 5) - hash + email.charCodeAt(index)) | 0;
  }

  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export function Members() {
  const { formatMessage } = useIntl();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedOrganization, hasRequestedOrganizationAccessDenied } = useOrganization();

  const organizationId = hasRequestedOrganizationAccessDenied
    ? ''
    : selectedOrganization?.organizationId ?? '';
  const orgName =
    selectedOrganization?.organizationName ?? formatMessage(messages.yourOrganization);
  const currentUser = useUserProfile();

  const orgMembersData = useGetWorkspaceMembers(
    { organizationId },
    { skip: organizationId.length === 0 },
  );
  const shouldAnimateMembersContent = organizationId.length > 0 && orgMembersData != null;

  const { cancelInvite } = useTeamApi(organizationId);
  const { hasRoleDefinition } = useRoles();

  // The workspace members API always returns the current user first.
  const canManage = useMemo(
    () => canManageMembers(orgMembersData?.members?.[0]),
    [orgMembersData?.members],
  );

  const canRemove = useMemo(
    () => canRemoveMembers(orgMembersData?.members?.[0]),
    [orgMembersData?.members],
  );

  const handleInvite = useCallback(() => {
    navigate(
      {
        pathname: `${ROUTE_TEAM}/invite`,
        search: location.search,
      },
      { state: { backgroundLocation: location } },
    );
  }, [location, navigate]);

  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusKey[]>([]);
  const [entityFilter, setEntityFilter] = useState<string[]>([]);
  const [productTabFilter, setProductTabFilter] = useState(''); // stores productKey (active tab)
  const [productChipFilter, setProductChipFilter] = useState<string[]>([]); // chip filter on All tab
  const [roleFilter, setRoleFilter] = useState<string[]>([]);
  const [memberPendingRemoval, setMemberPendingRemoval] = useState<TeamMember | null>(null);
  const hasLoggedSearchRef = useRef(false);

  useEffect(() => {
    logEvent('organization_team_page_viewed', {
      componentType: ComponentType.page,
      action: ActionType.view,
    });
  }, []);

  const { allMembers, rawMemberByUuid } = useMemo<{
    allMembers: UserCellMember[];
    rawMemberByUuid: Map<string, CoinbaseEngUsersAccountManagementApiApiWorkspaceMember>;
  }>(() => {
    const byEmail = new Map<string, UserCellMember>();
    const byUuid = new Map<string, CoinbaseEngUsersAccountManagementApiApiWorkspaceMember>();

    for (const member of orgMembersData?.members ?? []) {
      if (member.userUuid) {
        byUuid.set(member.userUuid, member);
      }

      const email = member.email ?? '';
      const roles = [
        ...new Set(
          (member.legalEntityRoles?.flatMap((ler) => ler.roles ?? []) ?? []).filter(
            hasRoleDefinition,
          ),
        ),
      ];

      const entities = [
        ...new Set(
          (member.legalEntityRoles ?? [])
            .map((ler) => ler.legalEntityName ?? ler.legalEntityId ?? '')
            .filter(Boolean),
        ),
      ];

      const existing = byEmail.get(email);
      if (existing) {
        const existingRoleIds = new Set(existing.roles);
        const nextRoles = roles.filter((role) => !existingRoleIds.has(role));
        byEmail.set(email, {
          ...existing,
          roles: [...existing.roles, ...nextRoles],
          entities: [...new Set([...(existing.entities ?? EMPTY_ARRAY), ...entities])],
        });
      } else {
        byEmail.set(email, {
          userUuid: member.userUuid,
          name: [member.firstName, member.lastName].filter(Boolean).join(' ') || email,
          email,
          avatarColor: getAvatarColor(email),
          avatarSrc: member.avatarUrl,
          roles,
          status: 'active',
          entities,
          isCurrentUser: member.email?.toLowerCase() === currentUser?.email?.toLowerCase(),
        });
      }
    }

    for (const invite of orgMembersData?.pendingInvites ?? []) {
      const email = invite.email ?? '';
      if (email) {
        const existing = byEmail.get(email);
        if (!existing) {
          byEmail.set(email, {
            userUuid: invite.userUuid,
            name: [invite.firstName, invite.lastName].filter(Boolean).join(' ') || email,
            email,
            avatarColor: getAvatarColor(email),
            roles: (invite.roles ?? []).filter(hasRoleDefinition),
            status: 'pending',
            entities: [],
            pendingInvites: [invite],
          });
        } else if (existing.status === 'pending') {
          byEmail.set(email, {
            ...existing,
            pendingInvites: [...(existing.pendingInvites ?? []), invite],
          });
        }
      }
    }

    const entries = [...byEmail.values()];
    const firstMember = entries[0];
    const rest = entries.slice(1).sort((a, b) => a.email.localeCompare(b.email));

    return {
      allMembers: firstMember ? [firstMember, ...rest] : [],
      rawMemberByUuid: byUuid,
    };
  }, [orgMembersData, currentUser?.email, hasRoleDefinition]);

  const {
    categoryTabs,
    allEntities,
    availableStatuses,
    availableEntities,
    availableRoles,
    availableProducts,
  } = useMemo(() => {
    const productMemberCount = new Map<string, number>();
    const entitySet = new Set<string>();
    const roleSet = new Set<string>();
    const statusSet = new Set<StatusKey>();

    for (const member of allMembers) {
      const seenProducts = new Set<string>();
      for (const role of member.roles) {
        const productKey = getProductKey(role);
        if (productKey && !seenProducts.has(productKey)) {
          seenProducts.add(productKey);
          productMemberCount.set(productKey, (productMemberCount.get(productKey) ?? 0) + 1);
        }
        if (!productTabFilter || productKey === productTabFilter) {
          roleSet.add(role);
        }
      }
      for (const entity of member.entities ?? EMPTY_ARRAY) {
        entitySet.add(entity);
      }
      statusSet.add(member.status);
    }

    const tabs = [...productMemberCount.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([id, count]) => ({ id, label: getProductDisplayName(id, formatMessage), count }));

    return {
      categoryTabs: tabs,
      allEntities: [...entitySet].sort(),
      availableStatuses: statusSet,
      availableEntities: entitySet,
      availableRoles: roleSet,
      availableProducts: new Set(productMemberCount.keys()),
    };
  }, [allMembers, formatMessage, productTabFilter]);

  const filteredMembers = useMemo(() => {
    let result = allMembers;

    if (searchValue) {
      const query = searchValue.toLowerCase();
      result = result.filter(
        (member) =>
          member.name.toLowerCase().includes(query) || member.email.toLowerCase().includes(query),
      );
    }

    if (statusFilter.length > 0) {
      result = result.filter((member) => statusFilter.includes(member.status));
    }

    if (entityFilter.length > 0) {
      result = result.filter((member) =>
        (member.entities ?? EMPTY_ARRAY).some((entity) => entityFilter.includes(entity)),
      );
    }

    if (productTabFilter) {
      // Category tab active: filter by the selected product
      result = result.filter((member) =>
        member.roles.some((role) => getProductKey(role) === productTabFilter),
      );
    } else if (productChipFilter.length > 0) {
      // All tab active: filter by the product chip selection
      result = result.filter((member) =>
        member.roles.some((role) => {
          const productKey = getProductKey(role);
          return productKey.length > 0 && productChipFilter.includes(productKey);
        }),
      );
    }

    if (roleFilter.length > 0) {
      result = result.filter((member) => member.roles.some((role) => roleFilter.includes(role)));
    }

    return result;
  }, [
    allMembers,
    entityFilter,
    productChipFilter,
    productTabFilter,
    roleFilter,
    searchValue,
    statusFilter,
  ]);

  const columns = useMemo<Column[]>(
    () => (productTabFilter ? ['name', 'entity', 'role', 'status'] : ORG_COLUMNS),
    [productTabFilter],
  );

  const paginationResetKey = useMemo(
    () =>
      JSON.stringify([
        searchValue,
        statusFilter,
        entityFilter,
        productTabFilter,
        productChipFilter,
        roleFilter,
      ]),
    [searchValue, statusFilter, entityFilter, productTabFilter, productChipFilter, roleFilter],
  );

  const allTab = useMemo(() => ({ id: ALL_TAB_ID, count: allMembers.length }), [allMembers.length]);

  // Display name for the active product tab, passed to TeamTable for role column rendering.
  const productDisplayName = productTabFilter
    ? getProductDisplayName(productTabFilter, formatMessage)
    : undefined;

  useEffect(() => {
    if (!organizationId || orgMembersData == null) {
      return;
    }

    logEvent('member_list_load_success', {
      componentType: ComponentType.api,
      action: ActionType.api,
    });
  }, [organizationId, orgMembersData]);

  useEffect(() => {
    let loadFailureTimeout: number | null = null;

    if (organizationId && orgMembersData == null) {
      loadFailureTimeout = window.setTimeout(() => {
        logEvent('member_list_load_failed', {
          componentType: ComponentType.api,
          action: ActionType.api,
          error: 'workspace_members_response_undefined',
        });
      }, 10000);
    }

    return () => {
      if (loadFailureTimeout != null) {
        window.clearTimeout(loadFailureTimeout);
      }
    };
  }, [organizationId, orgMembersData]);

  const handleProductFilterChange = useCallback((tab: string) => {
    logEvent('team_tab_clicked', {
      componentType: ComponentType.button,
      action: ActionType.click,
      tab_id: tab === ALL_TAB_ID ? ALL_TAB_ID : tab,
    });
    setProductTabFilter(tab === ALL_TAB_ID ? '' : tab);
    setRoleFilter([]); // reset role filter when switching tabs
    setProductChipFilter([]); // reset product chip filter when switching tabs
  }, []);

  useEffect(() => {
    const currentSearchValue = searchValue.trim();
    if (!currentSearchValue) {
      hasLoggedSearchRef.current = false;
      return;
    }
    if (hasLoggedSearchRef.current) {
      return;
    }

    logEvent('member_search_used', {
      componentType: ComponentType.search_bar,
      action: ActionType.search,
    });
    hasLoggedSearchRef.current = true;
  }, [searchValue]);

  const handleStatusFilterChange = useCallback((value: StatusKey | StatusKey[] | null) => {
    logEvent('member_filter_changed', {
      componentType: ComponentType.dropdown,
      action: ActionType.select,
      filter_type: 'status',
    });
    makeChipFilterChange(setStatusFilter)(value);
  }, []);
  const handleEntityFilterChange = useCallback((value: string | string[] | null) => {
    logEvent('member_filter_changed', {
      componentType: ComponentType.dropdown,
      action: ActionType.select,
      filter_type: 'entity',
    });
    makeChipFilterChange(setEntityFilter)(value);
  }, []);
  const handleProductChipFilterChange = useCallback((value: string | string[] | null) => {
    logEvent('member_filter_changed', {
      componentType: ComponentType.dropdown,
      action: ActionType.select,
      filter_type: 'product',
    });
    makeChipFilterChange(setProductChipFilter)(value);
  }, []);
  const handleRoleFilterChange = useCallback((value: string | string[] | null) => {
    logEvent('member_filter_changed', {
      componentType: ComponentType.dropdown,
      action: ActionType.select,
      filter_type: 'role',
    });
    makeChipFilterChange(setRoleFilter)(value);
  }, []);

  const handleMemberClick = useCallback(
    (member: UserCellMember, entryPoint = 'member_name') => {
      if (!member.userUuid) {
        logEvent('member_info_page_open_attempted_for_pending_invite', {
          componentType: ComponentType.table,
          action: ActionType.click,
          entry_point: entryPoint,
        });
        return;
      }

      logEvent('member_info_page_opened', {
        componentType: ComponentType.table,
        action: ActionType.click,
        entry_point: entryPoint,
      });
      navigate(getTeamMemberRoute(member.userUuid), {
        state: { backgroundLocation: location },
      });
    },
    [location, navigate],
  );

  const handleEditRole: (member: UserCellMember, productName: string) => void = useCallback(
    (member) => {
      handleMemberClick(member, 'overflow_menu');
    },
    [handleMemberClick],
  );

  const handleRemoveFromOrg = useCallback(
    (member: UserCellMember) => {
      if (!member.userUuid || !organizationId) {
        return;
      }

      const raw = rawMemberByUuid.get(member.userUuid);
      if (!raw) {
        return;
      }

      const teamMember: TeamMember = {
        userUuid: raw.userUuid ?? '',
        firstName: raw.firstName ?? '',
        lastName: raw.lastName ?? '',
        email: raw.email ?? '',
        roles: (raw.legalEntityRoles?.flatMap((ler) => ler.roles ?? []) ?? []) as MemberRole[],
        avatarUrl: raw.avatarUrl,
      };

      setMemberPendingRemoval(teamMember);
      logEvent('member_remove_clicked', {
        componentType: ComponentType.button,
        action: ActionType.click,
      });
    },
    [rawMemberByUuid, organizationId],
  );

  const handleCancelInvite = useCallback(
    async (member: UserCellMember) => {
      const pendingInvitesForUser = member.pendingInvites ?? [];
      if (!pendingInvitesForUser.length) return;

      try {
        await cancelInvite({
          userId: member.userUuid,
          email: member.email,
          legalEntityIds: pendingInvitesForUser
            .map((invite) => invite.legalEntityId)
            .filter((id): id is string => id != null),
        });
        logEvent('member_invite_cancelled_success', {
          componentType: ComponentType.api,
          action: ActionType.api,
        });
      } catch (error) {
        logEvent('member_invite_cancelled_failed', {
          componentType: ComponentType.api,
          action: ActionType.api,
          error: error instanceof Error ? error.message : String(error),
        });
        toast.show(
          formatMessage(
            getCancelInviteErrorMessage(error instanceof Error ? error.message : undefined),
          ),
        );
      }
    },
    [cancelInvite, toast, formatMessage],
  );

  const handleCloseRemoveMember = useCallback(() => {
    setMemberPendingRemoval(null);
  }, []);

  if (hasRequestedOrganizationAccessDenied) {
    return (
      <InAppErrorMessageFallback
        title={formatMessage(teamAccessMessages.deniedTitle)}
        description={formatMessage(teamAccessMessages.deniedDescription)}
      />
    );
  }

  if (!organizationId) {
    return (
      <InAppErrorMessageFallback
        title={formatMessage(messages.noOrgSelectedTitle)}
        description={formatMessage(messages.selectOrg)}
      />
    );
  }

  return (
    <VStack
      className={shouldAnimateMembersContent ? 'workspaceContentFadeIn' : undefined}
      gap={4}
      width="100%"
      data-testid="team-members-page"
    >
      <TeamHeader
        orgName={orgName}
        orgLogoSrc={ORG_LOGO_SRC}
        memberCount={allMembers.length}
        entities={allEntities}
        allTab={allTab}
        categoryTabs={categoryTabs}
        activeTab={productTabFilter || ALL_TAB_ID}
        onTabChange={handleProductFilterChange}
        onInvite={canManage ? handleInvite : undefined}
        isInviteDisabled={!canManage}
      />

      <TeamFilters
        availableEntities={availableEntities}
        availableProducts={availableProducts}
        availableRoles={availableRoles}
        availableStatuses={availableStatuses}
        columns={columns}
        entities={allEntities}
        entityFilter={entityFilter}
        onEntityFilterChange={handleEntityFilterChange}
        onProductFilterChange={handleProductChipFilterChange}
        onRoleFilterChange={handleRoleFilterChange}
        onSearchChange={setSearchValue}
        onStatusFilterChange={handleStatusFilterChange}
        productFilter={productChipFilter}
        products={categoryTabs}
        roleFilter={roleFilter}
        searchValue={searchValue}
        statusFilter={statusFilter}
      />

      <TeamTable
        members={filteredMembers}
        orgName={orgName}
        columns={columns}
        productName={productDisplayName}
        paginationResetKey={paginationResetKey}
        showActions
        canRemove={canRemove}
        canEdit={canManage}
        onNameClick={handleMemberClick}
        onRemoveFromOrg={handleRemoveFromOrg}
        onCancelInvite={handleCancelInvite}
        onEditRole={handleEditRole}
      />

      {memberPendingRemoval != null && (
        <RemoveMember
          visible
          member={memberPendingRemoval}
          organizationId={organizationId}
          onClose={handleCloseRemoveMember}
        />
      )}
    </VStack>
  );
}
